import * as AvatarPrimitive from "@radix-ui/react-avatar";
import cx from "classnames";
import { imageHelper } from "../../helpers";
import Tooltip from "./Tooltip";

export interface AvatarProps {
  /** Set a value here to display a status indicator badge (or set to `DO-NOT-SHOW` to not display one, which is the default setting) */
  activeStatus?: "ACTIVE" | "INACTIVE" | "DO-NOT-SHOW" | "UNKNOWN";
  /** The url to the avatar image */
  imageUrl: string | null | undefined;
  /** The name to display in a tooltip */
  userFullName: string;
  /** The initials to display if there is no image */
  userInitials: string;
  /** If you want to override the default size, specify a number and this will apply size classes like `h-10` and `w-10` (which are the defaults) */
  size?: number;
  /** If you increase the size of the avatar, you'll probably want to increase the size of the status badge, if not set to `DO-NOT-SHOW` */
  statusBadgeSize?: number;
  /** If you increase the size of the avatar, and need bigger text for the initials, supply a class name like `text-xl` */
  initialsTextClassName?: string;
  /** Width appended to image path */
  imagePathWidthSize?: number;
  /** This toggles PDF-friendly mode (so only set it if being used within PDF exports from HTML), specify a pixel size to avoid skewing */
  pdfPixelSize?: number;
}

const Avatar = ({
  activeStatus = "DO-NOT-SHOW",
  statusBadgeSize = 3,
  imageUrl,
  userFullName,
  userInitials,
  initialsTextClassName = "",
  size = 10,
  imagePathWidthSize = 500,
  pdfPixelSize = undefined,
}: AvatarProps) => {
  const showStatusBadge = activeStatus !== "DO-NOT-SHOW";
  let statusBadgeClassName = "";
  switch (activeStatus) {
    case "ACTIVE":
      statusBadgeClassName = "bg-green-600";
      break;
    case "INACTIVE":
      statusBadgeClassName = "bg-red-600";
      break;
    case "UNKNOWN":
      statusBadgeClassName = "bg-gray-300";
      break;
    default:
      break;
  }

  const imageFullPath = imageUrl
    ? imageHelper.getImagePath(imageUrl, imagePathWidthSize)
    : null;

  const isPdfFriendlyMode = pdfPixelSize && pdfPixelSize > 0;
  const pdfFriendlyClassNames = isPdfFriendlyMode
    ? `rounded-full overflow-hidden bg-center bg-cover`
    : "";
  const pdfFriendlyStyles: React.CSSProperties | undefined = isPdfFriendlyMode
    ? {
        backgroundImage: imageFullPath ? `url('${imageFullPath}')` : undefined,
        width: `${pdfPixelSize}px`,
        height: `${pdfPixelSize}px`,
      }
    : undefined;

  return (
    <Tooltip
      content={userFullName}
      triggerElement={
        <span>
          {isPdfFriendlyMode && (
            <div
              className={`relative inline-block ${pdfFriendlyClassNames}`}
              style={pdfFriendlyStyles}
            >
              {!imageFullPath && (
                <span
                  className={cx(
                    "rounded-full text-center bg-gray-200 shadow-lg flex items-center justify-center font-semibold select-none",
                    `w-${size} h-${size}`,
                    initialsTextClassName
                  )}
                >
                  {userInitials}
                </span>
              )}
            </div>
          )}
          {!isPdfFriendlyMode && (
            <AvatarPrimitive.Root>
              <div
                className={`relative inline-block w-${size} ${pdfFriendlyClassNames}`}
                style={pdfFriendlyStyles}
              >
                <AvatarPrimitive.Image
                  className={cx(
                    "inline object-cover rounded-full",
                    `w-${size} h-${size}`
                  )}
                  src={imageFullPath ? imageFullPath : undefined}
                  alt={userFullName}
                />
                <AvatarPrimitive.Fallback
                  className={cx(
                    "rounded-full text-center bg-gray-200 shadow-lg flex items-center justify-center font-semibold select-none",
                    `w-${size} h-${size}`,
                    initialsTextClassName
                  )}
                >
                  {userInitials}
                </AvatarPrimitive.Fallback>
                {showStatusBadge && (
                  <span
                    className={cx(
                      `absolute bottom-0 right-0 inline-block shadow-lg w-${statusBadgeSize} h-${statusBadgeSize} border-2 border-white rounded-full`,
                      statusBadgeClassName
                    )}
                  ></span>
                )}
              </div>
            </AvatarPrimitive.Root>
          )}
        </span>
      }
    />
  );
};

export default Avatar;
